import { ReactTFC } from "../../../../utils/fp/missing-ts-parts";
import React from "react";

import { css } from "@emotion/react";
import { CSS_MQ } from "../../../../styles/breakpoints";
import { CSS_COLORS } from "../../../../styles/styles-css-variables";
import { SwarovskiLink } from "../../../_shared-components/ui-elements/SwarovskiLink";
import { ContentfulImage } from "../../../_shared-components/image-related/ContentfulImage";
import { FONTS } from "../../../../styles/fonts";
import { ExpandedStoryTeaser } from "../../../../layouts/stories/stories-overview-page/stories-overview-page-layout";
import type { TranslatedValue } from "@swo/storefront";
import { T } from "../../../T";

export const StoriesOverviewMainTeaserWithoutMedia: React.FC<
  React.PropsWithChildren<
    Omit<ExpandedStoryTeaser, "imageWithFocalPoint" | "readingTime"> & {
      readingTime: undefined | string | TranslatedValue;
    }
  >
> = props => {
  const { seoHeadline, emoHeadline, link, readingTime, tags } = props;

  return (
    <div css={wrapperStyles}>
      <SwarovskiLink link={link} isInternal={true} scrollToTop styled={"no-decoration"}>
        {props.children}
        <div
          css={css`
            color: ${CSS_COLORS.White};
            position: relative;
            z-index: 2;

            ${CSS_MQ.atLeastMd} {
              max-width: 50%;
            }
          `}
        >
          <h2
            css={css`
              ${FONTS.SwarovskiFutura500};
              font-size: 17px;
              letter-spacing: 1px;
              text-transform: uppercase;
              margin-bottom: 8px;
            `}
          >
            {seoHeadline}
          </h2>
          <h3
            css={css`
              ${FONTS.MinervaModern500};

              font-size: 30px;
              line-height: 32px;
              letter-spacing: 1.5px;
              text-transform: uppercase;
              margin-top: 0;
              margin-bottom: 20px;
            `}
          >
            {emoHeadline}
          </h3>
        </div>
      </SwarovskiLink>

      {tags.length > 0 ? (
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            position: relative;
          `}
        >
          {tags.map((t, idx) => (
            <SwarovskiLink
              link={t.expandedLink}
              attachStyles={linkStyles}
              key={idx}
              styled={"no-decoration"}
              isInternal={true}
              scrollToTop={true}
            >
              {t.label}
            </SwarovskiLink>
          ))}
        </div>
      ) : null}
      {typeof readingTime !== "undefined" ? (
        <span css={readingTimeStyles}>
          <T>{readingTime}</T>
        </span>
      ) : null}
    </div>
  );
};

const readingTimeStyles = css`
  display: block;

  color: ${CSS_COLORS.White};
  margin-top: 8px;
  font-size: 16px;
  font-weight: 300;
  line-height: 1;
  position: relative;
  z-index: 2;
`;

const linkStyles = css`
  color: ${CSS_COLORS.White};
  margin-right: 5px;
  padding-right: 5px;
  font-weight: 500;
  line-height: 1;
  border-right: 1px solid ${CSS_COLORS.White};
  z-index: 2;
  &:last-child {
    border-right: none;
  }
`;

const wrapperStyles = css`
  position: relative;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-bottom: 40px;

  padding: 30px 15px;
  ${CSS_MQ.atLeastMd} {
    padding: 30px 25px;
  }

  &:before {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 200px;
    z-index: 1;
    background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.8));
  }

  ${CSS_MQ.atLeastSm} {
    margin-bottom: 20px;
  }
  ${CSS_MQ.atLeastLg} {
    margin-bottom: 45px;
  }
`;

export const StoriesOverviewMainTeaser: ReactTFC<ExpandedStoryTeaser> = props => {
  const { imageWithFocalPoint, ...rest } = props;

  return (
    <StoriesOverviewMainTeaserWithoutMedia {...rest}>
      <ContentfulImage
        assetId={imageWithFocalPoint.image.sys.id}
        contentfulLink={{
          url: imageWithFocalPoint.image.url,
          noTransparentBackground: true,
          requestedWidth: 1600,
        }}
        higherDPI={[2]}
        alt={imageWithFocalPoint.image.description || imageWithFocalPoint.image.title}
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        `}
        lazyLoaded={"eager"}
      />
    </StoriesOverviewMainTeaserWithoutMedia>
  );
};
